import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Typography } from 'antd';
import { getProject, getSeries, useSeriesCharacter as copySeriesCharacter } from '../illustration/api';
import { downloadUrlForStoragePath } from '../illustration/cloudProjects';
import { writesStayLocal } from '../illustration/localOnly';
import { shelfDisplayName, shelfPicture, shelfStoragePath } from './shelfPicture';
import './AddFromShelfPage.css';

const { Title, Paragraph } = Typography;

function alreadyInBook(card, characters) {
  const key = shelfDisplayName(card.name).toLowerCase();
  return (characters || []).some((item) => {
    if (item.meta?.seriesCharacterId === card.id) return true;
    const name = shelfDisplayName(item.meta?.name || item.title).toLowerCase();
    return name === key;
  });
}

export default function AddFromShelfPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [series, setSeries] = useState(null);
  const [picked, setPicked] = useState([]);
  const [pictures, setPictures] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    getProject(projectId)
      .then(async (data) => {
        if (cancel) return;
        const next = data.project;
        setProject(next);
        if (!next?.seriesId) {
          setSeries(null);
          return;
        }
        const shelf = await getSeries(next.seriesId);
        if (!cancel) setSeries(shelf.series || null);
      })
      .catch((err) => {
        if (!cancel) setError(err.message || 'Could not open the shelf');
      });
    return () => {
      cancel = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!series || writesStayLocal()) return undefined;
    let cancel = false;
    Promise.all((series.characters || []).map(async (card) => {
      const storagePath = shelfStoragePath(series.id, card);
      const url = storagePath ? await downloadUrlForStoragePath(storagePath) : '';
      return [card.id, url];
    })).then((rows) => {
      if (!cancel) setPictures(Object.fromEntries(rows));
    });
    return () => {
      cancel = true;
    };
  }, [series]);

  const characters = project?.structure?.characters || [];
  const cards = series?.characters || [];

  function toggle(card) {
    if (alreadyInBook(card, characters)) return;
    setPicked((current) => (
      current.includes(card.id) ? current.filter((id) => id !== card.id) : [...current, card.id]
    ));
  }

  async function addPicked() {
    if (!project?.seriesId || !picked.length) return;
    setSaving(true);
    setError('');
    try {
      let next = project;
      for (const seriesCharacterId of picked) {
        const result = await copySeriesCharacter(project.seriesId, project.id, seriesCharacterId);
        if (result?.project) next = result.project;
      }
      setProject(next);
      window.dispatchEvent(new Event('artificiallly:projects-changed'));
      navigate(`/projects/${projectId}/characters`);
    } catch (err) {
      setError(err.message || 'Could not add that person');
      const fresh = await getProject(projectId).catch(() => null);
      if (fresh?.project) setProject(fresh.project);
      setSaving(false);
    }
  }

  function skip() {
    navigate(`/projects/${projectId}/characters`);
  }

  return (
    <div className="workflow-shell__content">
      <Title level={1} style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
        Add people from the shelf
      </Title>
      <Paragraph style={{ maxWidth: 'var(--measure-prose)', color: 'var(--color-text-secondary)' }}>
        {series?.title
          ? `Choose who from ${series.title} goes into this book. Each person arrives as this book’s own copy.`
          : 'Choose who goes into this book.'}
      </Paragraph>
      {error ? <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} /> : null}
      {!project?.seriesId && project ? (
        <Paragraph>This book is not on a series.</Paragraph>
      ) : null}
      <div className="shelf-onboarding">
        {cards.map((card) => {
          const added = alreadyInBook(card, characters);
          const selected = picked.includes(card.id);
          const picture = pictures[card.id] || shelfPicture(card);
          return (
            <button
              key={card.id}
              type="button"
              className="shelf-onboarding__card"
              aria-pressed={selected}
              disabled={added}
              onClick={() => toggle(card)}
            >
              {picture ? (
                <img src={picture} alt="" />
              ) : (
                <span className="shelf-onboarding__empty" aria-hidden="true" />
              )}
              <span className="shelf-onboarding__name">{shelfDisplayName(card.name)}</span>
              <span className="shelf-onboarding__state">
                {added ? 'In this book' : selected ? 'Selected' : ''}
              </span>
            </button>
          );
        })}
        {series && !cards.length ? <Paragraph>No one is on the shelf yet.</Paragraph> : null}
      </div>
      <div className="shelf-onboarding__actions">
        <Button onClick={skip} disabled={saving}>Skip</Button>
        <Button type="primary" disabled={!picked.length} loading={saving} onClick={addPicked}>
          Add to book
        </Button>
      </div>
    </div>
  );
}
